package com.havn.app.features.agency.presentation

import com.havn.app.core.logging.HavnLogger
import com.havn.app.core.billing.HavnPlans
import com.havn.app.core.presentation.havnFriendlyMessage
import com.havn.app.features.agency.domain.Agency
import com.havn.app.features.agency.domain.AgencyInvitation
import com.havn.app.features.agency.domain.AgencyMember
import com.havn.app.features.agency.domain.AgencyRepository
import com.havn.app.features.agency.domain.AgencyRole
import com.havn.app.features.agency.domain.HavnSubscriptionTier
import com.havn.app.features.agency.domain.SubscriptionTransferRequest
import com.havn.app.features.agency.domain.UserEntitlement
import com.havn.app.features.auth.domain.AuthRepository
import com.havn.app.features.auth.domain.AuthState
import com.havn.app.core.media.HavnPickedPhoto
import com.havn.app.core.settings.loadStoredHavnSelectedAgencyId
import com.havn.app.core.settings.storeHavnSelectedAgencyId
import kotlinx.coroutines.CoroutineScope
import kotlinx.coroutines.Dispatchers
import kotlinx.coroutines.SupervisorJob
import kotlinx.coroutines.cancel
import kotlinx.coroutines.flow.MutableStateFlow
import kotlinx.coroutines.flow.StateFlow
import kotlinx.coroutines.flow.asStateFlow
import kotlinx.coroutines.launch
import kotlin.time.TimeMark
import kotlin.time.TimeSource
import kotlin.time.Duration.Companion.seconds

class AgencyViewModel(
    private val agencyRepository: AgencyRepository,
    private val authRepository: AuthRepository,
    private val scope: CoroutineScope = CoroutineScope(SupervisorJob() + Dispatchers.Main)
) {
    private val _uiState = MutableStateFlow(AgencyUiState())
    val uiState: StateFlow<AgencyUiState> = _uiState.asStateFlow()

    private var agentId: String? = null
    private var lastSubscriptionSync: TimeMark? = null

    init {
        scope.launch {
            authRepository.authState.collect { state ->
                val userId = (state as? AuthState.LoggedIn)?.user?.id
                if (agentId != userId) lastSubscriptionSync = null
                agentId = userId
                if (userId == null) {
                    _uiState.value = AgencyUiState()
                } else {
                    load(userId)
                }
            }
        }
    }

    fun refresh() {
        val userId = agentId ?: return
        scope.launch { load(userId) }
    }

    fun applyRevenueCatTier(tier: HavnSubscriptionTier) {
        val current = _uiState.value
        val limits = HavnPlans.limitsFor(tier)
        val entitlement = when (tier) {
            HavnSubscriptionTier.TeamAgent -> UserEntitlement(
                tier = HavnSubscriptionTier.TeamAgent,
                canUseAiFollowUps = limits.aiTextGenerationRequestsPerMonth > 0,
                canUseSellerReports = limits.hasSellerReports,
                canUseAdvancedSellerReports = limits.hasSellerReports,
                canUseAdvancedAnalytics = limits.hasAnalytics,
                canCreateAgency = limits.hasAgencyAgenda,
                canJoinAgency = limits.hasAgencyAgenda,
                maxActivePersonalProperties = limits.maxProperties,
                maxDocumentsPerProperty = limits.maxDocumentsPerProperty,
                documentsStorageBytes = limits.documentsStorageBytes,
                documentsStoragePerOwnerBytes = limits.documentsStorageBytes,
                maxFileSizeBytes = limits.maxFileSizeBytes
            )
            HavnSubscriptionTier.ProAgent -> UserEntitlement(
                tier = HavnSubscriptionTier.ProAgent,
                canUseAiFollowUps = limits.aiTextGenerationRequestsPerMonth > 0,
                canUseSellerReports = limits.hasSellerReports,
                canUseAdvancedSellerReports = limits.hasSellerReports,
                canUseAdvancedAnalytics = limits.hasAnalytics,
                canCreateAgency = limits.hasAgencyAgenda,
                canJoinAgency = limits.hasAgencyAgenda,
                maxActivePersonalProperties = limits.maxProperties,
                maxDocumentsPerProperty = limits.maxDocumentsPerProperty,
                documentsStorageBytes = limits.documentsStorageBytes,
                documentsStoragePerOwnerBytes = limits.documentsStorageBytes,
                maxFileSizeBytes = limits.maxFileSizeBytes
            )
            HavnSubscriptionTier.SoloAgent -> UserEntitlement(
                tier = HavnSubscriptionTier.SoloAgent,
                canUseAiFollowUps = limits.aiTextGenerationRequestsPerMonth > 0,
                canUseSellerReports = limits.hasSellerReports,
                maxActivePersonalProperties = limits.maxProperties,
                maxDocumentsPerProperty = limits.maxDocumentsPerProperty,
                documentsStorageBytes = limits.documentsStorageBytes,
                documentsStoragePerOwnerBytes = limits.documentsStorageBytes,
                maxFileSizeBytes = limits.maxFileSizeBytes
            )
            HavnSubscriptionTier.Free -> UserEntitlement()
        }
        _uiState.value = current.copy(
            entitlement = entitlement,
            hasLoadedEntitlement = true,
            error = null
        )
    }

    suspend fun syncRevenueCatSubscription(fallbackTier: HavnSubscriptionTier): Result<UserEntitlement> {
        applyRevenueCatTier(fallbackTier)
        return agencyRepository.syncRevenueCatSubscription()
            .onSuccess { entitlement ->
                _uiState.value = _uiState.value.copy(
                    entitlement = entitlement,
                    hasLoadedEntitlement = true,
                    error = null
                )
            }
            .onFailure { error ->
                HavnLogger.error("Agency", "RevenueCat subscription sync failed", error)
                _uiState.value = _uiState.value.copy(error = error.havnAgencyMessage("Could not refresh your subscription. Please try again."))
            }
    }

    suspend fun createSubscriptionTransfer(targetEmail: String): Result<SubscriptionTransferRequest> =
        agencyRepository.createSubscriptionTransfer(targetEmail)
            .onFailure { error ->
                HavnLogger.error("Agency", "Subscription transfer request failed", error)
                _uiState.value = _uiState.value.copy(error = error.havnAgencyMessage("Could not start the transfer. Please try again."))
            }

    suspend fun confirmSubscriptionTransfer(
        requestId: String,
        sourceCode: String,
        targetCode: String
    ): Result<Unit> =
        agencyRepository.confirmSubscriptionTransfer(requestId, sourceCode, targetCode)
            .onSuccess {
                refresh()
            }
            .onFailure { error ->
                HavnLogger.error("Agency", "Subscription transfer confirmation failed", error)
                _uiState.value = _uiState.value.copy(error = error.havnAgencyMessage("Could not confirm the transfer. Please try again."))
            }

    fun onAgencyNameChanged(value: String) {
        _uiState.value = _uiState.value.copy(newAgencyName = value, error = null)
    }

    fun onInviteEmailChanged(value: String) {
        _uiState.value = _uiState.value.copy(inviteEmail = value, error = null)
    }

    fun onInviteRoleChanged(value: AgencyRole) {
        _uiState.value = _uiState.value.copy(inviteRole = value, error = null)
    }

    fun onAgencyBrandingNameChanged(value: String) {
        _uiState.value = _uiState.value.copy(agencyBrandingName = value, error = null)
    }

    fun selectAgency(agencyId: String) {
        val state = _uiState.value
        if (!state.entitlement.isProAgent) {
            _uiState.value = state.copy(error = "Upgrade to Pro to use agency tools.")
            return
        }
        val selectedAgencyId = agencyId.takeIf { requestedId ->
            state.agencies.any { it.id == requestedId }
        } ?: state.agency?.id
        val selectedRole = state.agencies.firstOrNull { it.id == selectedAgencyId }?.role
        _uiState.value = state.copy(
            selectedAgencyId = selectedAgencyId,
            agencyBrandingName = state.agencies.firstOrNull { it.id == selectedAgencyId }?.name.orEmpty(),
            isActingLikeAgent = state.isActingLikeAgent && (selectedRole == AgencyRole.Owner || selectedRole == AgencyRole.Admin)
        )
        agentId?.let { storeHavnSelectedAgencyId(it, selectedAgencyId) }
        selectedAgencyId?.let { loadMembers(it) }
    }

    fun toggleActLikeAgent() {
        val state = _uiState.value
        if (!state.canActLikeAgent) return
        _uiState.value = state.copy(isActingLikeAgent = !state.isActingLikeAgent)
    }

    fun selectSoloWorkspace() {
        val state = _uiState.value
        _uiState.value = state.copy(selectedAgencyId = state.agency?.id, isActingLikeAgent = false)
        agentId?.let { storeHavnSelectedAgencyId(it, state.agency?.id) }
    }

    fun createAgency() {
        val userId = agentId ?: return
        val state = _uiState.value
        val name = state.newAgencyName.trim()
        if (name.length < 2) {
            _uiState.value = state.copy(error = "Agency name is required.")
            return
        }
        if (!state.entitlement.canCreateAgency) {
            _uiState.value = state.copy(error = "Upgrade to Pro to create and manage your agency.")
            return
        }
        if (state.hasAgency) {
            _uiState.value = state.copy(error = "Each agent has one agency.")
            return
        }

        _uiState.value = state.copy(isSaving = true, error = null)
        scope.launch {
            agencyRepository.createAgency(userId, name)
                .onSuccess { agency ->
                    _uiState.value = _uiState.value.copy(
                        agencies = (_uiState.value.agencies + agency).distinctBy { it.id },
                        selectedAgencyId = agency.id,
                        newAgencyName = "",
                        isSaving = false
                    )
                }
                .onFailure { error ->
                    HavnLogger.error("Agency", "Create agency failed", error)
                    _uiState.value = _uiState.value.copy(
                        isSaving = false,
                        error = error.havnAgencyMessage("Unable to create agency.")
                    )
                }
        }
    }

    fun inviteAgent() {
        val userId = agentId ?: return
        val state = _uiState.value
        val agency = state.selectedAgency ?: state.agency ?: return
        if (!state.entitlement.isProAgent) {
            _uiState.value = state.copy(error = "Upgrade to Pro to invite agents.")
            return
        }
        if (!agency.role.canManageMembers) {
            _uiState.value = state.copy(error = "Only owners and admins can invite agents.")
            return
        }
        _uiState.value = state.copy(isInviting = true, error = null)
        scope.launch {
            agencyRepository.inviteAgent(
                agencyId = agency.id,
                invitedByAgentId = userId,
                email = state.inviteEmail,
                role = state.inviteRole
            ).onSuccess {
                _uiState.value = _uiState.value.copy(
                    inviteEmail = "",
                    inviteRole = AgencyRole.Agent,
                    isInviting = false
                )
                loadMembers(agency.id)
            }.onFailure { error ->
                HavnLogger.error("Agency", "Invite agent failed", error)
                _uiState.value = _uiState.value.copy(
                    isInviting = false,
                    error = error.havnAgencyMessage("Unable to invite agent.")
                )
            }
        }
    }

    fun deleteInvite(invitationId: String) {
        val state = _uiState.value
        val agency = state.selectedAgency ?: state.agency ?: return
        if (!agency.role.canManageMembers) {
            _uiState.value = state.copy(error = "Only owners and admins can delete invites.")
            return
        }
        _uiState.value = state.copy(isDeletingInviteId = invitationId, error = null)
        scope.launch {
            agencyRepository.deleteInvite(invitationId)
                .onSuccess {
                    _uiState.value = _uiState.value.copy(
                        isDeletingInviteId = null,
                        members = _uiState.value.members.filterNot { it.invitationId == invitationId }
                    )
                    loadMembers(agency.id, showLoading = false)
                }
                .onFailure { error ->
                    HavnLogger.error("Agency", "Delete invite failed", error)
                    _uiState.value = _uiState.value.copy(
                        isDeletingInviteId = null,
                        error = error.havnAgencyMessage("Unable to cancel invitation.")
                    )
                }
        }
    }

    fun updateMemberRole(memberAgentId: String, role: AgencyRole) {
        val state = _uiState.value
        val agency = state.selectedAgency ?: state.agency ?: return
        if (!agency.role.canChangeRoles) {
            _uiState.value = state.copy(error = "Only owners and admins can change roles.")
            return
        }
        if (memberAgentId == agentId && agency.role == AgencyRole.Owner) {
            _uiState.value = state.copy(error = "You cannot change your own owner role.")
            return
        }
        _uiState.value = state.copy(updatingRoleAgentId = memberAgentId, error = null)
        scope.launch {
            agencyRepository.updateMemberRole(
                agencyId = agency.id,
                agentId = memberAgentId,
                role = role
            ).onSuccess {
                _uiState.value = _uiState.value.copy(
                    updatingRoleAgentId = null,
                    members = _uiState.value.members.map { member ->
                        if (member.agentId == memberAgentId) member.copy(role = role) else member
                    }
                )
                loadMembers(agency.id, showLoading = false)
            }.onFailure { error ->
                HavnLogger.error("Agency", "Update member role failed", error)
                _uiState.value = _uiState.value.copy(
                    updatingRoleAgentId = null,
                    error = error.havnAgencyMessage("Unable to update agent role.")
                )
            }
        }
    }

    fun removeMemberAccess(memberAgentId: String) {
        val state = _uiState.value
        val agency = state.selectedAgency ?: state.agency ?: return
        if (!agency.role.canManageMembers) {
            _uiState.value = state.copy(error = "Only owners and admins can remove access.")
            return
        }
        if (memberAgentId == agentId) {
            _uiState.value = state.copy(error = "You cannot remove your own agency access.")
            return
        }
        _uiState.value = state.copy(updatingRoleAgentId = memberAgentId, error = null)
        scope.launch {
            agencyRepository.removeMemberAccess(
                agencyId = agency.id,
                agentId = memberAgentId
            ).onSuccess {
                _uiState.value = _uiState.value.copy(
                    updatingRoleAgentId = null,
                    members = _uiState.value.members.filterNot { it.agentId == memberAgentId }
                )
                loadMembers(agency.id, showLoading = false)
            }.onFailure { error ->
                HavnLogger.error("Agency", "Remove member access failed", error)
                _uiState.value = _uiState.value.copy(
                    updatingRoleAgentId = null,
                    error = error.havnAgencyMessage("Unable to remove agency access.")
                )
            }
        }
    }

    fun saveAgencyBranding() {
        val state = _uiState.value
        val agency = state.selectedAgency ?: state.agency ?: return
        if (!agency.role.canManageBranding) {
            _uiState.value = state.copy(error = "Only owners and admins can change agency branding.")
            return
        }
        val name = state.agencyBrandingName.trim()
        if (name.length < 2) {
            _uiState.value = state.copy(error = "Agency name is required.")
            return
        }
        saveAgencyBrandingInternal(agency = agency, name = name, iconUrl = agency.iconUrl, logoPhoto = null, uploadingLogo = false)
    }

    fun uploadAgencyLogo(photo: HavnPickedPhoto?) {
        val state = _uiState.value
        val agency = state.selectedAgency ?: state.agency ?: return
        if (!agency.role.canManageBranding) {
            _uiState.value = state.copy(error = "Only owners and admins can change agency branding.")
            return
        }
        if (photo == null || photo.bytes.isEmpty()) {
            _uiState.value = state.copy(error = "Choose a valid logo image.")
            return
        }
        val name = state.agencyBrandingName.ifBlank { agency.name }
        saveAgencyBrandingInternal(agency = agency, name = name, iconUrl = agency.iconUrl, logoPhoto = photo, uploadingLogo = true)
    }

    fun acceptInvitation(invitationId: String) {
        respondToInvitation(
            invitationId = invitationId,
            action = AgencyInvitationAction.Accept
        )
    }

    fun declineInvitation(invitationId: String) {
        respondToInvitation(
            invitationId = invitationId,
            action = AgencyInvitationAction.Decline
        )
    }

    fun clearError() {
        _uiState.value = _uiState.value.copy(error = null)
    }

    fun dispose() {
        scope.cancel()
    }

    private suspend fun load(userId: String) {
        _uiState.value = _uiState.value.copy(isLoading = true, error = null)
        var entitlement = agencyRepository.entitlementForUser(userId)
        // Recover missed purchase notifications on sign-in/refresh on every platform.
        // Keep the stored entitlement usable if the billing provider is unavailable.
        if (entitlement.getOrNull()?.tier == HavnSubscriptionTier.Free &&
            lastSubscriptionSync?.elapsedNow()?.let { it < 60.seconds } != true
        ) {
            lastSubscriptionSync = TimeSource.Monotonic.markNow()
            agencyRepository.syncRevenueCatSubscription()
                .onSuccess { entitlement = Result.success(it) }
                .onFailure {
                    HavnLogger.error("Agency", "Automatic subscription sync failed; loading saved access", it)
                }
        }
        if (agentId != userId) return
        val agencies = agencyRepository.agenciesForUser(userId)
        val invitations = agencyRepository.pendingInvitationsForCurrentUser()
        val loadedEntitlement = entitlement.getOrElse { UserEntitlement() }
        val loadedAgencies = agencies.getOrElse { emptyList() }
        val loadedInvitations = invitations.getOrElse { emptyList() }
        if (agentId != userId) return
        val previousSelection = loadStoredHavnSelectedAgencyId(userId) ?: _uiState.value.selectedAgencyId
        val selectedAgencyId = if (loadedAgencies.isNotEmpty()) {
            previousSelection?.takeIf { selectedId ->
                loadedAgencies.any { it.id == selectedId }
            } ?: loadedAgencies.firstOrNull()?.id
        } else {
            null
        }
        storeHavnSelectedAgencyId(userId, selectedAgencyId)
        _uiState.value = _uiState.value.copy(
            entitlement = loadedEntitlement,
            agencies = loadedAgencies,
            pendingInvitations = loadedInvitations,
            selectedAgencyId = selectedAgencyId,
            agencyBrandingName = loadedAgencies.firstOrNull { it.id == selectedAgencyId }?.name.orEmpty(),
            isLoading = false,
            error = entitlement.exceptionOrNull()?.havnAgencyMessage("Could not load agency details.")
                ?: agencies.exceptionOrNull()?.havnAgencyMessage("Could not load agency details.")
                ?: invitations.exceptionOrNull()?.havnAgencyMessage("Could not load agency invitations."),
            hasLoadedEntitlement = true
        )
        selectedAgencyId?.let { loadMembers(it) }
    }

    private fun respondToInvitation(
        invitationId: String,
        action: AgencyInvitationAction
    ) {
        _uiState.value = _uiState.value.copy(respondingInvitationId = invitationId, error = null)
        scope.launch {
            val result = when (action) {
                AgencyInvitationAction.Accept -> agencyRepository.acceptInvite(invitationId)
                AgencyInvitationAction.Decline -> agencyRepository.declineInvite(invitationId)
            }
            result
                .onSuccess {
                    _uiState.value = _uiState.value.copy(respondingInvitationId = null)
                    agentId?.let { load(it) }
                }
                .onFailure { error ->
                    HavnLogger.error("Agency", "Agency invite response failed", error)
                    _uiState.value = _uiState.value.copy(
                        respondingInvitationId = null,
                        error = error.havnAgencyMessage("Unable to update agency invite.")
                    )
                }
        }
    }

    private fun saveAgencyBrandingInternal(
        agency: Agency,
        name: String,
        iconUrl: String?,
        logoPhoto: HavnPickedPhoto?,
        uploadingLogo: Boolean
    ) {
        _uiState.value = _uiState.value.copy(
            isSavingAgencyBranding = !uploadingLogo,
            isUploadingAgencyLogo = uploadingLogo,
            error = null
        )
        scope.launch {
            agencyRepository.updateAgencyBranding(
                agencyId = agency.id,
                name = name,
                iconUrl = iconUrl,
                logoPhoto = logoPhoto
            ).onSuccess { updated ->
                val merged = updated.copy(role = agency.role)
                _uiState.value = _uiState.value.copy(
                    agencies = _uiState.value.agencies.map { existing ->
                        if (existing.id == merged.id) merged else existing
                    },
                    selectedAgencyId = merged.id,
                    agencyBrandingName = merged.name,
                    isSavingAgencyBranding = false,
                    isUploadingAgencyLogo = false
                )
                agentId?.let { storeHavnSelectedAgencyId(it, merged.id) }
            }.onFailure { error ->
                HavnLogger.error("Agency", "Update agency branding failed", error)
                _uiState.value = _uiState.value.copy(
                    isSavingAgencyBranding = false,
                    isUploadingAgencyLogo = false,
                    error = error.havnAgencyMessage("Unable to update agency branding.")
                )
            }
        }
    }

    private fun loadMembers(agencyId: String, showLoading: Boolean = true) {
        if (showLoading) {
            _uiState.value = _uiState.value.copy(isLoadingMembers = true)
        }
        scope.launch {
            agencyRepository.membersForAgency(agencyId)
                .onSuccess { members ->
                    _uiState.value = _uiState.value.copy(
                        members = members,
                        isLoadingMembers = false
                    )
                }
                .onFailure { error ->
                    HavnLogger.error("Agency", "Load agency members failed", error)
                    _uiState.value = _uiState.value.copy(
                        isLoadingMembers = false,
                        error = error.havnAgencyMessage("Unable to load agency members.")
                    )
                }
        }
    }
}

private fun Throwable.havnAgencyMessage(fallback: String): String =
    havnFriendlyMessage(fallback)

data class AgencyUiState(
    val entitlement: UserEntitlement = UserEntitlement(),
    val agencies: List<Agency> = emptyList(),
    val members: List<AgencyMember> = emptyList(),
    val pendingInvitations: List<AgencyInvitation> = emptyList(),
    val selectedAgencyId: String? = null,
    val newAgencyName: String = "",
    val inviteEmail: String = "",
    val inviteRole: AgencyRole = AgencyRole.Agent,
    val agencyBrandingName: String = "",
    val isLoading: Boolean = false,
    val isLoadingMembers: Boolean = false,
    val isSaving: Boolean = false,
    val isInviting: Boolean = false,
    val isDeletingInviteId: String? = null,
    val updatingRoleAgentId: String? = null,
    val respondingInvitationId: String? = null,
    val isSavingAgencyBranding: Boolean = false,
    val isUploadingAgencyLogo: Boolean = false,
    val isActingLikeAgent: Boolean = false,
    val error: String? = null,
    val hasLoadedEntitlement: Boolean = false
) {
    val canOpenAgencyMode: Boolean
        get() = entitlement.isProAgent

    val agency: Agency?
        get() = agencies.firstOrNull()

    val selectedAgency: Agency?
        get() = agencies.firstOrNull { it.id == selectedAgencyId }

    val selectedAgencyRole: AgencyRole?
        get() = selectedAgency?.role ?: agency?.role

    val effectiveSelectedAgencyRole: AgencyRole?
        get() = if (isActingLikeAgent && canActLikeAgent) AgencyRole.Agent else selectedAgencyRole

    val canActLikeAgent: Boolean
        get() = selectedAgencyRole == AgencyRole.Owner || selectedAgencyRole == AgencyRole.Admin

    val workspaceName: String
        get() = selectedAgency?.name ?: agency?.name ?: SOLO_AGENCY_NAME

    val hasOwnedAgency: Boolean
        get() = agencies.any { it.role == com.havn.app.features.agency.domain.AgencyRole.Owner }

    val hasAgency: Boolean
        get() = agencies.isNotEmpty()

    companion object {
        const val SOLO_AGENCY_NAME = "Solo Agency"
    }
}

private enum class AgencyInvitationAction {
    Accept,
    Decline
}
